import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from '../../components/ui';
import { useAuth } from '../../contexts';
import { usePreferences } from '../../contexts/PreferencesContext';
import { api } from '../../services';
import logoClaro from '../../assets/Logo_claro.png';
import logoEscuro from '../../assets/Logo_escuro.png';
import styles from './Login.module.css';

export function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const [forgotMode, setForgotMode] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);
    const [forgotError, setForgotError] = useState('');

    const { login, isLoading } = useAuth();
    const navigate = useNavigate();
    const { t, prefs } = usePreferences();
    const darkMode = prefs.darkMode;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError(t('login.erroCampos'));
            return;
        }

        try {
            await login(email, password);
            navigate('/home');
        } catch {
            setError(t('login.erroCredenciais'));
        }
    };

    const handleForgotSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setForgotError('');
        if (!forgotEmail) {
            setForgotError(t('login.forgotEmailObrigatorio'));
            return;
        }
        setForgotLoading(true);
        try {
            await api.forgotPassword(forgotEmail);
            setForgotSent(true);
        } catch {
            setForgotError(t('login.forgotErro'));
        } finally {
            setForgotLoading(false);
        }
    };

    if (forgotMode) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.logo}>
                        <img
                            src={darkMode ? logoClaro : logoEscuro}
                            alt="Financeiro Diário"
                            className={styles.logoImg}
                        />
                    </div>

                    <div className={styles.header}>
                        <h1 className={styles.title}>{t('login.forgotTitulo')}</h1>
                        <p className={styles.subtitle}>{t('login.forgotSubtitulo')}</p>
                    </div>

                    {forgotSent ? (
                        <div className={styles.successMessage}>
                            {t('login.forgotSucesso')}
                        </div>
                    ) : (
                        <form className={styles.form} onSubmit={handleForgotSubmit}>
                            {forgotError && <div className={styles.error}>{forgotError}</div>}

                            <Input
                                type="email"
                                label={t('login.email')}
                                placeholder="seu@email.com"
                                value={forgotEmail}
                                onChange={(e) => setForgotEmail(e.target.value)}
                                fullWidth
                            />

                            <Button type="submit" fullWidth isLoading={forgotLoading}>
                                {t('login.forgotEnviar')}
                            </Button>
                        </form>
                    )}

                    <p className={styles.footer}>
                        <button
                            type="button"
                            className={styles.footerLink}
                            onClick={() => { setForgotMode(false); setForgotSent(false); setForgotError(''); setForgotEmail(''); }}
                        >
                            {t('login.voltarLogin')}
                        </button>
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.logo}>
                    <img
                        src={darkMode ? logoClaro : logoEscuro}
                        alt="Financeiro Diário"
                        className={styles.logoImg}
                    />
                </div>

                <div className={styles.header}>
                    <h1 className={styles.title}>{t('login.titulo')}</h1>
                    <p className={styles.subtitle}>{t('login.subtitulo')}</p>
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                    {error && <div className={styles.error}>{error}</div>}

                    <Input
                        type="email"
                        label={t('login.email')}
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                    />

                    <div>
                        <Input
                            type="password"
                            label={t('login.senha')}
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                        />
                        <div className={styles.forgotPassword}>
                            <button
                                type="button"
                                className={styles.forgotPasswordLink}
                                onClick={() => setForgotMode(true)}
                            >
                                {t('login.esqueceuSenha')}
                            </button>
                        </div>
                    </div>

                    <Button type="submit" fullWidth isLoading={isLoading}>
                        {t('login.entrar')}
                    </Button>
                </form>

                <p className={styles.footer}>
                    {t('login.semConta')}{' '}
                    <button
                        type="button"
                        className={styles.footerLink}
                        onClick={() => navigate('/register')}
                    >
                        {t('login.cadastrar')}
                    </button>
                </p>
            </div>
        </div>
    );
}
